import _ from 'lodash'
import moment from 'moment-business-days'
import momenttz from 'moment-timezone'

import {BusinessDay, BusinessHours} from '../../options/workCalendar.types'

import {DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT} from './dateTime.constants'
import {DateTimeDateRange, DaysOfTheMonth} from './types'

// Improved day mappings from mobile app - used for business day calculations
export const momentDayToBusinessDayMap = {
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
  0: 'SUN'
} as const

export const businessDayToMomentDayMap = {
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
  SUN: 0
} as const

export const getSelectableDateRange = (
  dateRange: DateTimeDateRange | undefined,
  businessHours: BusinessHours,
  businessDays: BusinessDay[]
) => {
  const timeZone = businessHours.timeZone
  const {min, max} = getShippingDateRange(dateRange, businessDays, timeZone)
  const minTime = businessHours?.earliestPossibleLoadTime
  const maxTime = businessHours?.latestPossibleLoadTime
  const localMinTime =
    minTime && timeZone ? convertToLocaleTime(minTime, timeZone, TIME_FORMAT) : minTime
  const localMaxTime =
    maxTime && timeZone ? convertToLocaleTime(maxTime, timeZone, TIME_FORMAT) : maxTime
  const minDate = moment(`${moment(min).format(DATE_FORMAT)} ${localMinTime}`, DATE_TIME_FORMAT)
  const maxDate = moment(`${moment(max).format(DATE_FORMAT)} ${localMaxTime}`, DATE_TIME_FORMAT)
  return {minDate, maxDate}
}

export const convertToLocaleTime = (time: string, timeZone: string, timeFormat: string) => {
  const currentTimeZone = momenttz().tz() ?? momenttz.tz.guess()
  return momenttz.tz(time, timeFormat, timeZone).tz(currentTimeZone).format(timeFormat)
}

export const setBusinessDays = (
  locale: string,
  businessDays: BusinessDay[],
  excludedDays?: string[]
): void => {
  const workingWeekdays = businessDays
    .filter((bd) => bd.isWorkingDay !== false)
    .filter((bd) => Object.keys(businessDayToMomentDayMap).includes(bd.dayOfWeek))
    .map((bd) => businessDayToMomentDayMap[bd.dayOfWeek as keyof typeof businessDayToMomentDayMap])
  moment.updateLocale(locale, {
    workingWeekdays,
    // parseZone keeps the sent calendar date; local parsing shifts UTC midnight a day back west of UTC.
    holidays: excludedDays?.map((ed) => moment.parseZone(ed).format('YYYY-MM-DD')),
    holidayFormat: 'YYYY-MM-DD',
    nextBusinessDayLimit: 90,
    prevBusinessDayLimit: 90
  })
}

export const getClosestBusinessDay = (referenceDate?: moment.Moment): moment.Moment => {
  const initialDate = referenceDate ? moment(referenceDate) : moment()
  const isTodayBusinessDay = initialDate.isBusinessDay()
  const nextBusinessDay = initialDate.businessAdd(1)

  return (() => {
    if (isTodayBusinessDay) {
      return initialDate.clone()
    }
    return nextBusinessDay.startOf('day')
  })()
}

export const isAfterLatestPossiblePlaceDate = (
  date: moment.Moment,
  leadTime?: number,
  cutOffTime?: string
) => {
  // Order Intake path: requires valid leadTime (> 0) and cutOffTime
  if (!leadTime || !cutOffTime) {
    return false
  }

  const cutOffTimeMoment = moment(cutOffTime)
  const startOfToday = moment().startOf('day')
  const minimumPlaceDate = date.businessSubtract(leadTime, 'days')
  const startOfMinimumPlaceDate = moment(minimumPlaceDate).startOf('day')
  const startOfCutOffTime = moment(cutOffTimeMoment).startOf('day')

  if (
    startOfMinimumPlaceDate.isBefore(startOfToday) ||
    startOfMinimumPlaceDate.isBefore(startOfCutOffTime)
  ) {
    return true
  }
  if (
    startOfMinimumPlaceDate.isSame(startOfCutOffTime) &&
    startOfMinimumPlaceDate.isSame(startOfToday)
  ) {
    return moment().isAfter(cutOffTimeMoment)
  }
  return false
}

export const isAfterBusinessHoursEnd = (
  day: moment.Moment,
  businessDays: BusinessDay[],
  timeZone: string
) => {
  const dayNameSort = momentDayToBusinessDayMap[day.day()]
  const businessDay = businessDays.find((bd) => dayNameSort === bd.dayOfWeek)
  if (businessDay && day.isBusinessDay()) {
    const currentMinDate = getClosestBusinessDay()

    const cutOffTimeValue = convertToLocaleTime(
      `${moment(currentMinDate).format('YYYY-MM-DD')} ${businessDay.businessHoursEnd}`,
      timeZone,
      DATE_TIME_FORMAT
    )

    return isAfterLatestPossiblePlaceDate(
      day,
      businessDay.leadTime,
      moment(cutOffTimeValue).toISOString()
    )
  }
  return true
}

// Same rule as the backend OrderChangeDateTimeValidator: the local date must not precede the
// weekday's earliest possible date in the business timezone.
const isBeforeEarliestPossibleDate = (
  day: moment.Moment,
  earliestPossibleDateTime: string,
  timeZone?: string
) => {
  const earliestPossible = timeZone
    ? momenttz.utc(earliestPossibleDateTime).tz(timeZone)
    : moment(earliestPossibleDateTime)
  return day.format(DATE_FORMAT) < earliestPossible.format(DATE_FORMAT)
}

export const isOutOfLeadTime = (
  day: moment.Moment,
  businessDays: BusinessDay[],
  leadTime?: number | 'leadTime' | 'autoConfirmLeadTime',
  timeZone?: string
) => {
  const dayNameSort = momentDayToBusinessDayMap[day.day()]
  const businessDay = businessDays.find((bd) => dayNameSort === bd.dayOfWeek)

  // Order Management options carry no leadTime/cutOffTime: the backend already resolved them
  // into a per-weekday earliestPossibleDateTime (null = no slot left for that weekday).
  if (businessDay?.isWorkingDay !== undefined) {
    if (!businessDay.isWorkingDay || !businessDay.earliestPossibleDateTime) {
      return true
    }
    return isBeforeEarliestPossibleDate(day, businessDay.earliestPossibleDateTime, timeZone)
  }

  const relevantLeadTime = _.isNumber(leadTime) ? leadTime : businessDay?.[leadTime ?? 'leadTime']
  if (relevantLeadTime === undefined) {
    return true
  }

  if (businessDay && day.isBusinessDay()) {
    const cutOffTimeValue =
      typeof businessDay.cutOffTime === 'object'
        ? businessDay.cutOffTime?.timestamp
        : businessDay.cutOffTime

    // Order Intake path: requires valid leadTime (> 0) and cutOffTime
    const cutOffDateTime = moment(
      `${moment().format(DATE_FORMAT)} ${cutOffTimeValue}`,
      DATE_TIME_FORMAT
    )

    return isAfterLatestPossiblePlaceDate(day, relevantLeadTime, cutOffDateTime.toISOString())
  }
  return true
}

export const isShippingDateOutOfLeadTime = (
  shippingDate: moment.Moment,
  businessDays: BusinessDay[],
  timeZone?: string
) => {
  return isOutOfLeadTime(shippingDate, businessDays, 'leadTime', timeZone)
}

export const isShippingDateOutOfAutoConfirmLeadTime = (
  shippingDate: moment.Moment,
  businessDays: BusinessDay[]
) => {
  return isOutOfLeadTime(shippingDate, businessDays, 'autoConfirmLeadTime')
}

export const isDateTimeValid = (
  date: moment.Moment,
  minDate: moment.Moment | undefined,
  maxDate: moment.Moment | undefined,
  businessDays: BusinessDay[],
  timeZone?: string
) => {
  return (
    date.isBusinessDay() &&
    !isShippingDateOutOfLeadTime(date, businessDays, timeZone) &&
    date.isBetween(minDate, maxDate, 'second', '[]')
  )
}

export const isDateValid = (
  date: moment.Moment,
  minDate: moment.Moment | undefined,
  maxDate: moment.Moment | undefined,
  businessDays: BusinessDay[],
  timeZone?: string
) => {
  const isOutOfLeadTime = isShippingDateOutOfLeadTime(date, businessDays, timeZone)
  const isInRange = date.isBetween(moment(minDate), moment(maxDate), 'day', '[]')
  const isBusinessDay = date.isBusinessDay()
  return isBusinessDay && isInRange && !isOutOfLeadTime
}

// Without a contract range, falls back to the lead-time based min and the 90-day max (same as OnSite).
export const getShippingDateRange = (
  dateRange: DateTimeDateRange | undefined,
  businessDays: BusinessDay[],
  timeZone?: string
) => {
  const maxMoment = moment().startOf('day').add(90, 'days').endOf('month')

  const minMoment = (() => {
    let currentMinDate = getClosestBusinessDay()
    if (businessDays && businessDays.length > 0) {
      // Bounded so a calendar where every weekday is out of lead time cannot loop forever.
      while (
        currentMinDate.isBefore(maxMoment) &&
        isOutOfLeadTime(currentMinDate, businessDays, 'leadTime', timeZone)
      ) {
        currentMinDate = currentMinDate.businessAdd(1)
      }
      return moment(currentMinDate).startOf('day')
    }
    return currentMinDate.startOf('day')
  })()
  const fromMoment = dateRange?.from ? moment(dateRange.from).startOf('day') : minMoment

  const toMoment = (() => {
    let lastDate = moment(dateRange?.to ?? maxMoment)
    while (lastDate.isHoliday() && lastDate.isAfter(minMoment)) {
      lastDate = lastDate.businessAdd(-1)
    }
    return lastDate
  })()

  const minDate = fromMoment.isAfter(minMoment) ? fromMoment : minMoment
  const maxDate = toMoment.isAfter(maxMoment) ? maxMoment : toMoment

  return {
    min: minDate.isBusinessDay() ? minDate : minDate.nextBusinessDay(),
    max: maxDate.isBusinessDay() ? maxDate : maxDate.prevBusinessDay()
  }
}

export const getFirstDayOfTheMonth = (dateOfTheMonth: string) => {
  return moment(dateOfTheMonth).startOf('month').startOf('day')
}

export const getDaysOfTheMonths = (
  dayOfTheMonth: string,
  businessDays: BusinessDay[],
  timeZone: string
) => {
  const firstDay = moment(dayOfTheMonth).startOf('month').startOf('week')
  const lastDay = moment(dayOfTheMonth).endOf('month').endOf('week')
  const daysInMonth: DaysOfTheMonth = []

  const currentDay = firstDay.clone()
  while (currentDay.isSameOrBefore(lastDay, 'day')) {
    if (
      currentDay.format('YYYY-MM-DD') === currentDay.clone().startOf('week').format('YYYY-MM-DD')
    ) {
      daysInMonth.push([])
    }

    const currentWeek = daysInMonth[daysInMonth.length - 1]
    if (currentWeek) {
      currentWeek.push({
        dayOfTheMonth: currentDay.format('DD'),
        dateString: currentDay.format('YYYY-MM-DD'),
        isAfterCutOffTime: isOutOfLeadTime(currentDay, businessDays, 'leadTime', timeZone),
        isAfterBusinessHoursEnd: isAfterBusinessHoursEnd(currentDay, businessDays, timeZone),
        isAfterAutoConfirmLeadTime: isOutOfLeadTime(
          currentDay,
          businessDays,
          'autoConfirmLeadTime',
          timeZone
        ),
        isExtra:
          currentDay.isBefore(moment(dayOfTheMonth).startOf('month'), 'day') ||
          currentDay.isAfter(moment(dayOfTheMonth).endOf('month'), 'day')
      })
    }
    currentDay.add(1, 'day')
  }
  return daysInMonth
}
